import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { financeAPI, formatApiErrorDetail } from '../../lib/api';
import { 
  Wallet, Plus, ArrowUp, ArrowDown, Bank, Receipt, 
  CaretDown, CaretUp, Scales 
} from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const ACCOUNT_TYPES = ['general', 'project', 'charitable', 'operational', 'reserve'];

const FinancePage = () => {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [filterAccount, setFilterAccount] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const [accountForm, setAccountForm] = useState({
    name: '', account_type: 'general', description: '', initial_balance: ''
  });

  const [transactionForm, setTransactionForm] = useState({
    account_id: '', transaction_type: 'deposit', amount: '', description: '',
    date: new Date().toISOString().split('T')[0], reference: '',
    contributor_name: '', recipient_name: '', notes: ''
  });

  const fetchData = async () => {
    try {
      const [accountsRes, transactionsRes, summaryRes] = await Promise.all([
        financeAPI.getAccounts(),
        financeAPI.getTransactions(),
        financeAPI.getSummary()
      ]);
      setAccounts(accountsRes.data);
      setTransactions(transactionsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await financeAPI.createAccount({
        ...accountForm,
        initial_balance: parseFloat(accountForm.initial_balance) || 0
      });
      toast.success('Account created successfully');
      setShowAccountModal(false);
      setAccountForm({ name: '', account_type: 'general', description: '', initial_balance: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      await financeAPI.createTransaction({
        ...transactionForm,
        amount: parseFloat(transactionForm.amount)
      });
      toast.success('Transaction recorded successfully');
      setShowTransactionModal(false);
      setTransactionForm({
        account_id: '', transaction_type: 'deposit', amount: '', description: '',
        date: new Date().toISOString().split('T')[0], reference: '',
        contributor_name: '', recipient_name: '', notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const filteredTransactions = transactions.filter(t => {
    const matchesAccount = filterAccount === 'all' || t.account_id === filterAccount;
    const matchesType = filterType === 'all' || t.transaction_type === filterType;
    return matchesAccount && matchesType;
  });

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.name || 'Unknown';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="finance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Scales size={32} className="text-[#7B1818]" />
            <div>
              <h1 className="font-heading text-3xl text-[#1A1614]">Financial Ledger</h1>
              <p className="text-[#8A847A]">Manage accounts, deposits, and withdrawals</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowAccountModal(true)} 
              variant="outline"
              className="rounded-none border-[#C5A059] text-[#C5A059]"
              data-testid="add-account-button"
            >
              <Bank size={18} className="mr-2" /> New Account
            </Button>
            <Button 
              onClick={() => setShowTransactionModal(true)} 
              className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none"
              data-testid="add-transaction-button"
            >
              <Receipt size={18} className="mr-2" /> New Transaction
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white border border-[#D8D2C4] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#8A847A]">Total Balance</p>
                    <p className="text-2xl font-heading text-[#1A1614]">
                      ${summary?.total_balance?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}
                    </p>
                  </div>
                  <Wallet size={32} className="text-[#C5A059]" />
                </div>
              </div>
              <div className="bg-white border border-[#D8D2C4] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#8A847A]">Accounts</p>
                    <p className="text-2xl font-heading text-[#1A1614]">{summary?.account_count || 0}</p>
                  </div>
                  <Bank size={32} className="text-[#7B1818]" />
                </div>
              </div>
              <div className="bg-white border border-[#D8D2C4] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#8A847A]">Total Deposits</p>
                    <p className="text-2xl font-heading text-green-600">
                      ${summary?.accounts?.reduce((sum, a) => sum + a.total_deposits, 0)?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}
                    </p>
                  </div>
                  <ArrowDown size={32} className="text-green-600" />
                </div>
              </div>
              <div className="bg-white border border-[#D8D2C4] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#8A847A]">Total Withdrawals</p>
                    <p className="text-2xl font-heading text-red-600">
                      ${summary?.accounts?.reduce((sum, a) => sum + a.total_withdrawals, 0)?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}
                    </p>
                  </div>
                  <ArrowUp size={32} className="text-red-600" />
                </div>
              </div>
            </div>

            {/* Accounts */}
            <div>
              <h2 className="font-heading text-xl text-[#1A1614] mb-4">Accounts</h2>
              {accounts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {accounts.map(account => (
                    <div 
                      key={account.id} 
                      className="bg-white border border-[#D8D2C4] p-6 hover:border-[#C5A059] transition-colors cursor-pointer"
                      onClick={() => setSelectedAccount(account)}
                      data-testid={`account-card-${account.id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-overline text-xs mb-1">{account.account_type?.toUpperCase()}</p>
                          <h3 className="font-heading text-lg text-[#1A1614]">{account.name}</h3>
                        </div>
                        <Bank size={24} className="text-[#8A847A]" />
                      </div>
                      {account.description && (
                        <p className="text-sm text-[#4A453E] mb-4 line-clamp-2">{account.description}</p>
                      )}
                      <div className="pt-4 border-t border-[#E8E4D9]">
                        <p className="text-sm text-[#8A847A]">Current Balance</p>
                        <p className={`text-2xl font-heading ${account.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${account.current_balance?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-[#D8D2C4] p-8 text-center text-[#8A847A]">
                  No accounts created yet. Click "New Account" to get started.
                </div>
              )}
            </div>

            {/* Transactions */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <h2 className="font-heading text-xl text-[#1A1614]">Transaction Ledger</h2>
                <div className="flex gap-3">
                  <Select value={filterAccount} onValueChange={setFilterAccount}>
                    <SelectTrigger className="w-48 rounded-none">
                      <SelectValue placeholder="All Accounts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Accounts</SelectItem>
                      {accounts.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-40 rounded-none">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="deposit">Deposits</SelectItem>
                      <SelectItem value="withdrawal">Withdrawals</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredTransactions.length > 0 ? (
                <div className="bg-white border border-[#D8D2C4] overflow-x-auto">
                  <table className="w-full data-table" data-testid="transactions-table">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Type</th>
                        <th className="px-4 py-3 text-left">Account</th>
                        <th className="px-4 py-3 text-left">Description</th>
                        <th className="px-4 py-3 text-left">Reference</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map(txn => (
                        <tr key={txn.id} data-testid={`transaction-row-${txn.id}`}>
                          <td className="px-4 py-3 text-[#4A453E]">
                            {new Date(txn.date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`flex items-center gap-2 ${txn.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.transaction_type === 'deposit' ? (
                                <><ArrowDown size={16} /> Deposit</>
                              ) : (
                                <><ArrowUp size={16} /> Withdrawal</>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#4A453E]">{getAccountName(txn.account_id)}</td>
                          <td className="px-4 py-3 text-[#1A1614]">
                            {txn.description}
                            {txn.contributor_name && <span className="text-[#8A847A] text-sm block">From: {txn.contributor_name}</span>}
                            {txn.recipient_name && <span className="text-[#8A847A] text-sm block">To: {txn.recipient_name}</span>}
                          </td>
                          <td className="px-4 py-3 text-[#8A847A]">{txn.reference || '-'}</td>
                          <td className={`px-4 py-3 text-right font-medium ${txn.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.transaction_type === 'deposit' ? '+' : '-'}${txn.amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-white border border-[#D8D2C4] p-8 text-center text-[#8A847A]">
                  No transactions recorded yet.
                </div>
              )}
            </div>
          </>
        )}

        {/* Create Account Modal */}
        <Dialog open={showAccountModal} onOpenChange={setShowAccountModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Create New Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAccount} className="space-y-4" data-testid="account-form">
              <div>
                <label className="block text-sm font-medium mb-1">Account Name *</label>
                <Input
                  value={accountForm.name}
                  onChange={(e) => setAccountForm({...accountForm, name: e.target.value})}
                  required
                  placeholder="e.g., General Fund, Charitable Fund"
                  className="rounded-none"
                  data-testid="account-input-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Account Type</label>
                  <Select 
                    value={accountForm.account_type} 
                    onValueChange={(v) => setAccountForm({...accountForm, account_type: v})}
                  >
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map(t => (
                        <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Opening Balance</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={accountForm.initial_balance}
                    onChange={(e) => setAccountForm({...accountForm, initial_balance: e.target.value})}
                    placeholder="0.00"
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={accountForm.description}
                  onChange={(e) => setAccountForm({...accountForm, description: e.target.value})}
                  rows={2}
                  placeholder="Purpose of this account"
                  className="rounded-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAccountModal(false)} className="rounded-none">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="account-submit-button">
                  Create Account
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Transaction Modal */}
        <Dialog open={showTransactionModal} onOpenChange={setShowTransactionModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Record Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTransaction} className="space-y-4" data-testid="transaction-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Account *</label>
                  <Select 
                    value={transactionForm.account_id} 
                    onValueChange={(v) => setTransactionForm({...transactionForm, account_id: v})}
                  >
                    <SelectTrigger className="rounded-none" data-testid="transaction-select-account">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <Select 
                    value={transactionForm.transaction_type} 
                    onValueChange={(v) => setTransactionForm({...transactionForm, transaction_type: v})}
                  >
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deposit">
                        <span className="flex items-center gap-2 text-green-600">
                          <ArrowDown size={14} /> Deposit
                        </span>
                      </SelectItem>
                      <SelectItem value="withdrawal">
                        <span className="flex items-center gap-2 text-red-600">
                          <ArrowUp size={14} /> Withdrawal
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Amount *</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                    required
                    placeholder="0.00"
                    className="rounded-none"
                    data-testid="transaction-input-amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <Input
                    type="date"
                    value={transactionForm.date}
                    onChange={(e) => setTransactionForm({...transactionForm, date: e.target.value})}
                    required
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <Input
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                  required
                  placeholder="What is this transaction for?"
                  className="rounded-none"
                  data-testid="transaction-input-description"
                />
              </div>
              {transactionForm.transaction_type === 'deposit' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Contributor Name</label>
                  <Input
                    value={transactionForm.contributor_name}
                    onChange={(e) => setTransactionForm({...transactionForm, contributor_name: e.target.value})}
                    placeholder="Who made this deposit?"
                    className="rounded-none"
                  />
                </div>
              )}
              {transactionForm.transaction_type === 'withdrawal' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Recipient/Payee</label>
                  <Input
                    value={transactionForm.recipient_name}
                    onChange={(e) => setTransactionForm({...transactionForm, recipient_name: e.target.value})}
                    placeholder="Who received this payment?"
                    className="rounded-none"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Reference Number</label>
                <Input
                  value={transactionForm.reference}
                  onChange={(e) => setTransactionForm({...transactionForm, reference: e.target.value})}
                  placeholder="Check #, Receipt #, Invoice #"
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Textarea
                  value={transactionForm.notes}
                  onChange={(e) => setTransactionForm({...transactionForm, notes: e.target.value})}
                  rows={2}
                  placeholder="Additional notes"
                  className="rounded-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowTransactionModal(false)} className="rounded-none">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className={`rounded-none ${transactionForm.transaction_type === 'deposit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="transaction-submit-button"
                >
                  {transactionForm.transaction_type === 'deposit' ? 'Record Deposit' : 'Record Withdrawal'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FinancePage;
